import { useEffect, useRef } from 'react';

import { useRecoilValue } from 'recoil';

import { useVersionCheck } from 'lib/hooks/useVersionCheck';

import { showAlert } from 'components/shared/Alert/Alert';
import { hasBeenAuthChecked as hasBeenAuthCheckedState } from 'components/state';

import { claimAutoReload, clearReloadTarget } from './lib/reloadGuard';

const AutoUpdate = () => {
  const hasBeenAuthChecked = useRecoilValue(hasBeenAuthCheckedState);
  const { status, reloadPage, checkUpdate, UpdateStatus, versions } = useVersionCheck({
    interval: 5 * 60 * 1000,
    type: 'interval',
    ignoreServerCache: true
  });
  const announcedRef = useRef(UpdateStatus.current);
  const reloadTimeoutRef = useRef(null);
  const latestVersion = versions?.latestVersion;

  const cancelPendingReload = () => {
    if (reloadTimeoutRef.current) {
      clearTimeout(reloadTimeoutRef.current);
      reloadTimeoutRef.current = null;
    }
  };

  useEffect(() => cancelPendingReload, []);

  useEffect(() => {
    let lastFocusCheck = 0;
    const handleFocus = () => {
      if (document.hidden) return;
      const now = Date.now();
      if (now - lastFocusCheck < 10 * 60 * 1000) return;
      lastFocusCheck = now;
      checkUpdate();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [checkUpdate]);

  useEffect(() => {
    if (status === UpdateStatus.current) {
      // Reset so a release after a rollback announces again instead of being
      // swallowed by the pre-rollback announcement.
      announcedRef.current = UpdateStatus.current;
      cancelPendingReload();
      clearReloadTarget();
      return;
    }

    if (status <= announcedRef.current) return;
    announcedRef.current = status;

    const promptToReload = (color, title, message) =>
      showAlert({
        color,
        title,
        message,
        position: 'bottom-center',
        footerActions: {
          primary: {
            text: 'Reload Page',
            onClick: reloadPage
          }
        },
        duration: 9999999
      });

    const reloadWithNotice = (color, message) => {
      if (!claimAutoReload(latestVersion)) {
        promptToReload(
          color,
          'An update is available',
          'Reload when you are ready to pick up the latest version.'
        );
        return;
      }

      showAlert({
        color,
        title: 'An update is available',
        message,
        position: 'bottom-center',
        duration: 9999999
      });
      reloadTimeoutRef.current = setTimeout(() => {
        reloadPage();
      }, 2000);
    };

    switch (status) {
      case UpdateStatus.available:
        promptToReload(
          'primary',
          'An update is available',
          'Please update at your earliest convenience for the best experience.'
        );
        break;
      case UpdateStatus.major:
        // The atom holds { checked, isImpersonate } — the object itself is always truthy.
        if (!hasBeenAuthChecked?.checked) {
          reloadWithNotice('primary', 'Reloading to get the latest version...');
          break;
        }
        promptToReload(
          'warning',
          'A major update is available',
          'Please update as soon as possible.'
        );
        break;
      case UpdateStatus.critical:
        reloadWithNotice('danger', 'This update will automatically reload your page.');
        break;
      default:
        break;
    }
  }, [status, latestVersion, hasBeenAuthChecked, reloadPage, UpdateStatus]);

  return null;
};

export default AutoUpdate;
